import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EditDialog, RetireDialog } from "@/components/RecordEditor";
import { UserPlus, Search, KeyRound, PencilLine, Ban, Undo2, ShieldCheck, Copy } from "lucide-react";

const NEW_USER = {
  name: "", email: "", password: "", role: "tech", job_title: "", phone: "",
  initials: "", employee_id: "", start_date: "", must_change_password: true,
};

const ROLE_TONE = {
  admin: "bg-[#134e4a] text-white",
  lab_director: "bg-[#134e4a] text-white",
  pathologist: "bg-sky-800 text-white",
  pathologist_assistant: "bg-sky-100 text-sky-900",
  supervisor: "bg-amber-700 text-white",
  tech: "bg-slate-200 text-slate-800",
  lab_assistant: "bg-slate-100 text-slate-700",
  viewer: "bg-slate-100 text-slate-600",
};

export default function UserAccounts() {
  const { user: me } = useAuth();
  const [roles, setRoles] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ q: "", role: "", status: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...NEW_USER });
  const [busy, setBusy] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [deactivateUser, setDeactivateUser] = useState(null);
  const [matrixOpen, setMatrixOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      const { data } = await api.get("/users", { params });
      setUsers(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => {
    api.get("/roles").then((r) => { setRoles(r.data.roles); setAssignable(r.data.assignable); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy("create");
    try {
      const { data } = await api.post("/users", { ...form, start_date: form.start_date || null });
      toast.success(`${data.name} added as ${data.role_label}`);
      setOpen(false);
      setForm({ ...NEW_USER });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const changeRole = async (u, role) => {
    try {
      const { data } = await api.put(`/users/${u.id}`, { role });
      toast.success(`${u.name} is now ${data.role_label}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resetPassword = async () => {
    if (newPassword.length < 6) return toast.error("Use at least 6 characters");
    try {
      await api.post(`/users/${resetUser.id}/reset-password`, { password: newPassword, must_change_password: true });
      toast.success(`Temporary password set for ${resetUser.name} — hand it over now`);
      setResetUser(null);
      setNewPassword("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const activate = async (u) => {
    try {
      await api.post(`/users/${u.id}/activate`);
      toast.success(`${u.name} reactivated`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const randomPassword = () => {
    const pool = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
    const pick = Array.from({ length: 10 }, () => pool[Math.floor(Math.random() * pool.length)]).join("");
    setForm((f) => ({ ...f, password: `${pick}!` }));
  };

  return (
    <div className="space-y-6" data-testid="user-accounts-page">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Staff accounts</h2>
          <p className="mt-1 text-sm text-slate-600">
            {users.length} account(s). Create accounts, set roles, reset passwords and deactivate leavers —
            accounts are never deleted so signatures on past reports stay valid.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setMatrixOpen(true)} data-testid="permissions-matrix-button"
                  className="h-11 border-slate-300">
            <ShieldCheck size={15} className="mr-2" /> What each role can do
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="new-user-button"
                  className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
            <UserPlus size={15} className="mr-2" /> Create account
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3">
        <div className="min-w-[220px] flex-1">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Search</Label>
          <div className="relative mt-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                   data-testid="user-search-input" placeholder="Name, email, job title or employee ID"
                   className="h-11 border-slate-300 bg-white pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Role</Label>
          <select value={filters.role} onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
                  data-testid="user-role-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All roles</option>
            {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  data-testid="user-status-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">Active + deactivated</option>
            <option value="active">Active only</option>
            <option value="inactive">Deactivated only</option>
          </select>
        </div>
        <Button variant="outline" data-testid="user-clear-filters" className="h-11 border-slate-300"
                onClick={() => setFilters({ q: "", role: "", status: "" })}>Clear</Button>
      </div>

      <div className="overflow-x-auto border border-slate-200">
        <table className="w-full min-w-[980px] text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
            <tr className="border-b border-slate-200">
              <th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th><th className="px-3 py-2">Job title</th>
              <th className="px-3 py-2">Status</th><th className="px-3 py-2">Last sign-in</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={`border-b border-slate-100 row-hover hover:bg-slate-50 ${u.active ? "" : "bg-slate-50/70"}`}
                  data-testid={`user-row-${u.email}`}>
                <td className="px-3 py-2 font-medium text-slate-800">
                  {u.name}
                  {u.initials && <span className="ml-2 font-mono text-[11px] text-slate-500">{u.initials}</span>}
                  {u.id === me?.id && <span className="ml-2 text-[11px] uppercase text-slate-500">you</span>}
                  {u.must_change_password && u.active && (
                    <div className="text-[11px] text-amber-800" data-testid={`user-temp-password-${u.email}`}>
                      temporary password — must change at next sign-in
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-600">{u.email}</td>
                <td className="px-3 py-2">
                  <select value={u.role} onChange={(e) => changeRole(u, e.target.value)}
                          data-testid={`role-select-${u.email}`}
                          className="h-10 w-44 rounded-md border border-slate-300 bg-white px-2 text-sm">
                    {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                  <span className={`ml-2 hidden px-1.5 py-0.5 text-[11px] font-semibold lg:inline ${ROLE_TONE[u.role]}`}>
                    {u.role_label}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-600">{u.job_title || "—"}</td>
                <td className="px-3 py-2">
                  {u.active
                    ? <span className="text-green-800">Active</span>
                    : (
                      <span className="text-slate-600" data-testid={`user-inactive-${u.email}`}>
                        Deactivated
                        <div className="text-[11px] text-slate-500">
                          {u.deactivated_by} {u.deactivated_at ? fmtDateTime(u.deactivated_at) : ""}
                          {u.deactivated_reason ? ` — ${u.deactivated_reason}` : ""}
                        </div>
                      </span>
                    )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{u.last_login_at ? fmtDateTime(u.last_login_at) : "never"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => setEditUser(u)}
                            data-testid={`edit-user-${u.email}`} className="h-9">
                      <PencilLine size={14} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setResetUser(u); setNewPassword(""); }}
                            data-testid={`reset-password-${u.email}`} className="h-9">
                      <KeyRound size={14} />
                    </Button>
                    {u.active ? (
                      <Button size="sm" variant="outline" onClick={() => setDeactivateUser(u)}
                              disabled={u.id === me?.id} data-testid={`deactivate-user-${u.email}`}
                              className="h-9 border-red-300 text-red-800 hover:bg-red-50">
                        <Ban size={14} />
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => activate(u)}
                              data-testid={`activate-user-${u.email}`} className="h-9">
                        <Undo2 size={14} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="users-empty">
                No accounts match these filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="new-user-dialog">
          <DialogHeader>
            <DialogTitle>Create a staff account</DialogTitle>
            <DialogDescription>
              Set a temporary password and hand it over — they will be asked to change it at first sign-in.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Full name</Label>
                <Input required minLength={2} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                       data-testid="user-name-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Work email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                       data-testid="user-email-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Role</Label>
                <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                        data-testid="user-role-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {assignable.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  {assignable.find((r) => r.key === form.role)?.description}
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Temporary password</Label>
                <div className="mt-1 flex gap-2">
                  <Input required minLength={6} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                         data-testid="user-password-input" className="h-11 border-slate-300 font-mono" />
                  <Button type="button" variant="outline" onClick={randomPassword}
                          data-testid="generate-password-button" className="h-11 border-slate-300">
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Job title</Label>
                <Input value={form.job_title} onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
                       data-testid="user-job-title-input" placeholder="e.g. Senior Histotechnologist" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Initials (report stamps)</Label>
                <Input value={form.initials} onChange={(e) => setForm((f) => ({ ...f, initials: e.target.value }))}
                       data-testid="user-initials-input" className="mt-1 h-11 border-slate-300 font-mono uppercase" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Employee ID</Label>
                <Input value={form.employee_id} onChange={(e) => setForm((f) => ({ ...f, employee_id: e.target.value }))}
                       data-testid="user-employee-id-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                       data-testid="user-phone-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                       data-testid="user-start-date-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.must_change_password} data-testid="user-must-change-input"
                     onChange={(e) => setForm((f) => ({ ...f, must_change_password: e.target.checked }))}
                     className="h-4 w-4 accent-[#134e4a]" />
              Require a password change at first sign-in
            </label>
            <Button type="submit" disabled={busy === "create"} data-testid="create-user-submit"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "create" ? "Creating…" : "Create account"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <EditDialog
        open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)} testid="edit-user-dialog"
        title={`Edit ${editUser?.name || ""}`} submitLabel="Save account"
        description="Correct a name, email or staff detail. Role changes and password resets are separate actions."
        values={editUser || {}}
        fields={[
          { name: "name", label: "Full name" },
          { name: "email", label: "Work email" },
          { name: "role", label: "Role", type: "select", options: roles.map((r) => ({ value: r.key, label: r.label })) },
          { name: "job_title", label: "Job title" },
          { name: "initials", label: "Initials" },
          { name: "employee_id", label: "Employee ID" },
          { name: "phone", label: "Phone" },
          { name: "start_date", label: "Start date", type: "date" },
        ]}
        onSave={async (body) => {
          await api.put(`/users/${editUser.id}`, body);
          toast.success("Account updated");
          load();
        }}
      />

      <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password — {resetUser?.name}</DialogTitle>
            <DialogDescription>
              Type a temporary password and hand it over in person. They must change it at next sign-in.
            </DialogDescription>
          </DialogHeader>
          <Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={6}
                 data-testid="reset-password-input" className="h-11 border-slate-300 font-mono"
                 placeholder="Temporary password" />
          <Button onClick={resetPassword} data-testid="confirm-reset-password-button"
                  className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">Set temporary password</Button>
        </DialogContent>
      </Dialog>

      <RetireDialog
        open={!!deactivateUser} onOpenChange={(v) => !v && setDeactivateUser(null)} testid="deactivate-user-dialog"
        title={`Deactivate ${deactivateUser?.name || ""}`} confirmLabel="Deactivate account"
        description="They can no longer sign in, but their name stays on every case, report and record they touched."
        placeholder="Reason — e.g. left the department on 30 June"
        onConfirm={async (reason) => {
          await api.post(`/users/${deactivateUser.id}/deactivate`, { reason });
          toast.success("Account deactivated");
          load();
        }}
      />

      <Dialog open={matrixOpen} onOpenChange={setMatrixOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="permissions-matrix-dialog">
          <DialogHeader>
            <DialogTitle>What each role can do</DialogTitle>
            <DialogDescription>Roles are fixed so the audit trail stays meaningful across inspections.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {roles.map((r) => (
              <section key={r.key} className="border border-slate-200 p-3" data-testid={`role-card-${r.key}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold text-slate-900">{r.label}</h3>
                  <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${ROLE_TONE[r.key]}`}>{r.key}</span>
                  <span className="num ml-auto text-xs text-slate-500">
                    {users.filter((u) => u.role === r.key && u.active).length} active
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{r.description}</p>
                <ul className="mt-2 grid gap-1 text-sm text-slate-700 sm:grid-cols-2">
                  {r.permissions.map((p) => <li key={p} className="flex gap-2"><span className="text-[#134e4a]">·</span>{p}</li>)}
                </ul>
              </section>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
